import { Popover } from '@/components/utils/Popover/Popover';
import { capture } from '@/handlers/error';
import { useAudio } from '@/hooks/useAudio';
import { useAudioGenerator } from '@/hooks/useAudioGenerator/useAudioGenerator';
import { SlideComponentFileModel } from '@/types/slide';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import { Box, Button, CircularProgress, Divider, IconButton, List, ListItem, ListItemButton, ListItemText, MenuItem, Skeleton, Stack, TextField, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'rooks';
import { useCountUp } from 'use-count-up';
import { AudioRecorderView } from './AudioRecorderView';

type SourceType = 'record' | 'upload' | 'ai';

const SIZE = {
    width: 640,
    height: 456,
};
const PROGRESS_LACING_RATE = 100;
const ICON_SIZE = {
    width: 100,
    height: 100,
    // please adjust progress accordingly based on width and height above
    progress: 84,
};

interface Props {
    title: string;
    anchorEl: HTMLElement | null;
    value: SlideComponentFileModel;
    onClose: () => void;
    onUpdate: (componentFile: SlideComponentFileModel) => void;
    onUpload?: (file: File) => Promise<SlideComponentFileModel | null>;
}

export const AudioRecorderPopover: React.FC<Props> = ({ title, anchorEl, onClose, onUpdate, onUpload, value }) => {
    const { t } = useTranslation();
    const notifications = useNotifications();

    const { toggle, stop, playing, duration } = useAudio(value.url);
    const { value: progress, reset } = useCountUp({
        isCounting: playing,
        duration: duration,
        start: 0,
        end: 100 * PROGRESS_LACING_RATE,
        easing: 'linear',
    });

    const open = Boolean(anchorEl);

    const [source, setSource] = useState<SourceType>(value.ai?.text ? 'ai' : 'record');
    const [uploading, setUploading] = useState(false);

    const { generateAudio } = useAudioGenerator();

    const onGenAudio = React.useCallback(
        async (voice: string, text: string) => {
            try {
                setUploading(true);
                const audioFile = await generateAudio(voice, text);
                const res = await onUpload?.(audioFile);
                if (res) {
                    onUpdate({ ...value, id: res.id, filename: res.filename, url: res.url, ai: { voice: value.ai?.voice ?? '', text: value.ai?.text ?? '' } });
                }
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setUploading(false);
            }
        },
        [generateAudio, onUpload, onUpdate, value, notifications],
    );

    const onUploadChange = React.useCallback(
        async (audioFiles: File[]) => {
            try {
                setUploading(true);

                if (audioFiles && audioFiles.length > 0) {
                    const audioFile = audioFiles[0];
                    if (!audioFile) {
                        throw new Error('No valid audio file selected');
                    }

                    const res = await onUpload?.(audioFile);
                    if (res) {
                        onUpdate({ ...value, id: res.id, filename: res.filename, url: res.url, ai: { voice: '', text: '' } });
                    }
                }
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setUploading(false);
            }
        },
        [onUpload, onUpdate, value, notifications],
    );

    const onRecordChange = React.useCallback(
        async (audioFile: File) => {
            if (uploading) {
                return;
            }

            try {
                setUploading(true);

                const res = await onUpload?.(audioFile);
                if (res) {
                    onUpdate({ ...value, id: res.id, filename: res.filename, url: res.url, ai: { voice: '', text: '' } });
                }
            } catch (error) {
                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                capture(error);
            } finally {
                setUploading(false);
            }
        },
        [onUpload, onUpdate, value, uploading, notifications],
    );

    useEffect(() => {
        if (!playing) {
            reset();
        }
    }, [playing, reset]);

    const debouncedUpdate = useDebounce(onUpdate, 500);

    return (
        <Popover
            title={title}
            open={open}
            anchorEl={anchorEl}
            onClose={() => {
                stop();
                onClose();
            }}
            anchorOrigin={{
                vertical: 'center',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'center',
                horizontal: -8,
            }}
            slotProps={{
                paper: {
                    sx: {
                        width: SIZE.width,
                        height: SIZE.height,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 2,
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Stack spacing={1.5} sx={{ flex: 1 }} direction={'row'}>
                    <List
                        sx={(theme) => ({
                            width: 220,
                            border: 1,
                            borderColor: (theme.vars || theme).palette.divider,
                            borderRadius: 1,
                            overflow: 'hidden',
                            p: 0,
                        })}
                    >
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={source === 'record'}
                                onClick={() => {
                                    setSource('record');
                                }}
                            >
                                <ListItemText primary={t('slide.components.assets.audio.record')} />
                            </ListItemButton>
                        </ListItem>
                        <Divider />
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={source === 'upload'}
                                onClick={() => {
                                    setSource('upload');
                                }}
                            >
                                <ListItemText primary={t('slide.components.assets.audio.upload')} />
                            </ListItemButton>
                        </ListItem>
                        <Divider />
                        <ListItem disablePadding>
                            <ListItemButton
                                selected={source === 'ai'}
                                onClick={() => {
                                    setSource('ai');
                                }}
                            >
                                <ListItemText primary={t('slide.components.assets.audio.ai')} />
                            </ListItemButton>
                        </ListItem>
                    </List>

                    {value.url && (
                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                flex: 1,
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: 1,
                                borderRadius: 0.5,
                                borderStyle: 'dashed',
                                height: '100%',
                                borderColor: (theme.vars || theme).palette.grey[300],
                                backgroundColor: (theme.vars || theme).palette.grey[100],
                                ...theme.applyStyles('dark', {
                                    backgroundColor: (theme.vars || theme).palette.grey[700],
                                }),
                            })}
                        >
                            <Stack alignItems='center' spacing={1.5}>
                                {!duration ? (
                                    <Skeleton
                                        variant='circular'
                                        sx={{
                                            width: ICON_SIZE.width,
                                            height: ICON_SIZE.height,
                                        }}
                                        aria-label='skeleton'
                                    />
                                ) : (
                                    <IconButton
                                        onClick={() => {
                                            toggle();
                                        }}
                                        sx={{
                                            width: ICON_SIZE.width,
                                            height: ICON_SIZE.height,
                                        }}
                                    >
                                        {!playing ? (
                                            <PlayCircleIcon
                                                sx={{
                                                    width: ICON_SIZE.width,
                                                    height: ICON_SIZE.height,
                                                }}
                                                data-testid='PlayCircleIcon'
                                            />
                                        ) : (
                                            <StopCircleIcon
                                                sx={{
                                                    width: ICON_SIZE.width,
                                                    height: ICON_SIZE.height,
                                                }}
                                                data-testid='StopCircleIcon'
                                            />
                                        )}
                                        <CircularProgress
                                            variant='determinate'
                                            value={(progress as number) / PROGRESS_LACING_RATE}
                                            size={ICON_SIZE.progress}
                                            sx={{
                                                position: 'absolute',
                                                '.MuiCircularProgress-circle': {
                                                    transition: 'none',
                                                },
                                            }}
                                        />
                                    </IconButton>
                                )}

                                <Button
                                    variant='outlined'
                                    startIcon={source === 'record' ? <GraphicEqIcon /> : <FileUploadIcon />}
                                    sx={{ width: 'fit-content', alignSelf: 'center' }}
                                    onClick={() => onUpdate({ ...value, id: '', filename: '', url: '' })}
                                >
                                    {source === 'ai'
                                        ? t('slide.components.assets.audio.aiVoice.editText')
                                        : source === 'record'
                                          ? t('slide.components.assets.audio.recordAgain')
                                          : t('slide.components.assets.audio.uploadAgain')}
                                </Button>
                            </Stack>
                        </Box>
                    )}

                    {source === 'record' && !value.url && <AudioRecorderView onUploadAudio={onRecordChange} />}

                    {source === 'upload' && !value.url && (
                        <>
                            {uploading ? (
                                <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Dropzone
                                    onDropAccepted={onUploadChange}
                                    onDropRejected={() => notifications.show(t('slide.components.assets.audio.uploadRejected'), { severity: 'warning', autoHideDuration: 5000 })}
                                    accept={{
                                        'audio/mpeg': ['.mp3'],
                                    }}
                                    maxFiles={1}
                                    multiple={false}
                                >
                                    {({ getRootProps, getInputProps }) => (
                                        <Box
                                            sx={(theme) => ({
                                                p: 1,
                                                flex: 1,
                                                border: 1,
                                                borderRadius: 0.5,
                                                borderStyle: 'dashed',
                                                borderColor: (theme.vars || theme).palette.grey[300],
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                height: '100%',
                                                backgroundColor: (theme.vars || theme).palette.grey[100],
                                                ...theme.applyStyles('dark', {
                                                    backgroundColor: (theme.vars || theme).palette.grey[700],
                                                }),
                                            })}
                                            {...getRootProps({ className: 'dropzone' })}
                                        >
                                            <input {...getInputProps()} />
                                            <Stack direction='column' spacing={1.5} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                <Typography>{t('slide.components.assets.audio.dropYourFileHere')}</Typography>
                                                <Typography>{t('slide.components.assets.audio.or')}</Typography>

                                                <Button variant='outlined' startIcon={<FileUploadIcon />}>
                                                    <Typography sx={{ textAlign: 'center' }}>{t('slide.components.assets.audio.uploadHere')}</Typography>
                                                </Button>
                                            </Stack>
                                        </Box>
                                    )}
                                </Dropzone>
                            )}
                        </>
                    )}

                    {source === 'ai' && !value.url && (
                        <Stack spacing={1} sx={{ flex: 1 }}>
                            <TextField
                                label={t('slide.components.assets.audio.aiVoice.title')}
                                select
                                fullWidth
                                value={value.ai?.voice}
                                onChange={(e) => {
                                    onUpdate({ ...value, ai: { ...value.ai, voice: e.target.value as string, text: value.ai?.text ?? '' } });
                                }}
                            >
                                {[
                                    { value: 'girl', label: t('slide.components.assets.audio.aiVoice.girl') },
                                    { value: 'boy', label: t('slide.components.assets.audio.aiVoice.boy') },
                                    { value: 'female', label: t('slide.components.assets.audio.aiVoice.female') },
                                    { value: 'male', label: t('slide.components.assets.audio.aiVoice.male') },
                                    { value: 'seniorFemale', label: t('slide.components.assets.audio.aiVoice.seniorFemale') },
                                    { value: 'seniorMale', label: t('slide.components.assets.audio.aiVoice.seniorMale') },
                                ].map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Box
                                sx={() => ({
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                })}
                            >
                                <Stack sx={{ flex: 1, height: '100%' }} spacing={1}>
                                    <TextField
                                        multiline
                                        defaultValue={value.ai?.text ?? ''}
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                            '& .MuiInputBase-root': {
                                                height: '100%',
                                                alignItems: 'flex-start',
                                            },
                                            '& .MuiInputBase-input': {
                                                height: '100% !important',
                                                overflow: 'auto !important',
                                            },
                                        }}
                                        slotProps={{ htmlInput: { maxLength: 300 } }}
                                        onChange={(e) => {
                                            debouncedUpdate({ ...value, ai: { ...value.ai, voice: value.ai?.voice ?? 'female', text: e.target.value } });
                                        }}
                                    />

                                    <Button
                                        variant='contained'
                                        onClick={async () => {
                                            onGenAudio(value.ai?.voice ?? '', value.ai?.text ?? '');
                                        }}
                                        startIcon={<AutoAwesomeIcon />}
                                        disabled={uploading || !value.ai?.text}
                                    >
                                        {t('slide.components.assets.audio.aiVoice.convert')}
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </Stack>
            </Box>
        </Popover>
    );
};
